<template>
  <div class="container-fluid px-4 mt-4">
    <!-- Fullscreen loading -->
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p>Yükleniyor...</p>
    </div>

    <h4 class="mb-4 font-weight-bold text-dark text-center">
      İşletme Kar Raporu
    </h4>

    <!-- Filtre Formu -->
    <b-row class="justify-content-center">
      <b-col cols="12" md="10" lg="8">
        <b-form @submit.prevent="downloadReport" class="p-3 bg-white border rounded">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Başlangıç Tarihi">
                <b-form-datepicker
                    id="date-from"
                    v-model="reportForm.from"
                    placeholder="Başlangıç tarihi seçiniz"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group label="Bitiş Tarihi">
                <b-form-datepicker
                    id="date-to"
                    v-model="reportForm.to"
                    placeholder="Bitiş tarihi seçiniz"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group label="İşletmeler">
                <multiselect
                    v-model="reportForm.clientIds"
                    :options="clients"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="true"
                    :preserve-search="true"
                    track-by="id"
                    label="name"
                    placeholder="İşletme seçin (boş bırakılırsa tümü seçilir)"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Tek buton: Excel İndir -->
          <div class="d-flex justify-content-end mt-3">
            <b-button
                variant="success"
                type="submit"
                :disabled="isDownloadDisabled"
            >
              Excel İndir
            </b-button>
          </div>

          <div class="text-muted small mt-2">
            Başlangıç ve bitiş tarihini doldurmanız gerekir.
          </div>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import ReportService from "@/service/ReportService";
import * as XLSX from "xlsx-js-style";
import Multiselect from "vue-multiselect";
import ClientService from "@/service/ClientService";

export default {
  name: "shop-profit-report",
  components: { Multiselect },

  data() {
    return {
      loading: false,
      reportForm: {
        from: null,
        to: null,
        clientIds: [],
      },
      clients: [],
    };
  },

  computed: {
    isDownloadDisabled() {
      return this.loading || !this.reportForm.from || !this.reportForm.to;
    },
  },

  methods: {
    async downloadReport() {
      if (!this.reportForm.from || !this.reportForm.to) {
        this.$bvToast?.toast("Başlangıç ve bitiş tarihini doldurmanız gerekiyor.", {
          variant: "warning",
        });
        return;
      }

      this.loading = true;

      try {
        const selectedIds = this.reportForm.clientIds?.length
            ? this.reportForm.clientIds.map((c) => c.id)
            : this.clients.map((c) => c.id);

        const payload = {
          from: this.reportForm.from,
          to: this.reportForm.to,
          clientIds: selectedIds,
        };

        const res = await ReportService.getShopProfitReport(payload);
        const report = res?.data ?? res;

        if (!report || !Array.isArray(report.rows) || report.rows.length === 0) {
          this.$bvToast?.toast("Kayıt bulunamadı.", { variant: "warning" });
          return;
        }

        this.exportToExcel(report);
      } catch (e) {
        this.$bvToast?.toast("Rapor çekilirken bir hata oluştu.", { variant: "danger" });
      } finally {
        this.loading = false;
      }
    },

    exportToExcel(report) {
      const rows = report.rows || [];

      if (!rows.length) {
        this.$bvToast?.toast("Excel oluşturmak için yeterli veri yok.", { variant: "warning" });
        return;
      }

      // ---------- Header ----------
      const headerRow = ["İşletme Adı", "Toplam Kar"];

      // ---------- Data ----------
      const dataRows = rows.map((row) => {
        let profit = row.totalProfit != null ? row.totalProfit : 0;
        if (typeof profit === "string") {
          const num = Number(profit.replace(",", "."));
          profit = isNaN(num) ? profit : num;
        }
        return [row.clientName || "", profit];
      });

      // ---------- AOA ve sheet ----------
      const aoa = [headerRow, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);

      // ---------- Kolon genişlikleri ----------
      const headerLength = "İşletme Adı".length;
      const maxClientNameLength = Math.max(
          headerLength,
          ...rows.map((r) => (r.clientName || "").length)
      );
      worksheet["!cols"] = [
        { wch: maxClientNameLength + 2 }, // İşletme Adı — dinamik genişlik
        { wch: 18 },                       // Toplam Kar
      ];

      // ---------- Stil: başlık bold ----------
      const encodeCell = XLSX.utils.encode_cell;

      for (let c = 0; c < 2; c++) {
        const addr = encodeCell({ r: 0, c });
        const cell = worksheet[addr];
        if (!cell) continue;
        cell.s = {
          font: { bold: true },
        };
      }

      // ---------- Stil: totalProfit sayı formatı #,##0.00 ----------
      for (let r = 1; r < aoa.length; r++) {
        const addr = encodeCell({ r, c: 1 });
        const cell = worksheet[addr];
        if (!cell || cell.v == null) continue;

        let val = cell.v;
        if (typeof val === "string") {
          const num = Number(val.replace(",", "."));
          if (!isNaN(num)) val = num;
        }
        if (typeof val === "number") {
          cell.v = val;
          cell.t = "n";
          cell.z = "#,##0.00";
        }
      }

      // ---------- Workbook ----------
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Kar Raporu");

      const fileName = `isletme_kar_raporu_${this.reportForm.from}_${this.reportForm.to}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    },

    async loadClients() {
      try {
        this.loading = true;
        const requestBody = {
          columnFilters: {},
          page: 1,
          perPage: 10000,
          sortField: "name",
          sortDirection: "asc",
        };
        const response = await ClientService.list(requestBody);
        this.clients =
            response.data.map((item) => ({
              ...item,
              name: item.shortName ? `${item.shortName} (${item.name})` : item.name,
            })) || [];
      } catch (error) {
        console.error("İşletme listesi alınırken hata oluştu:", error);
        this.$errorNotification &&
        this.$errorNotification(this, "İşletme listesi alınırken hata oluştu.");
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    await this.loadClients();
  },
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
